import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useLiff } from '../hooks/useLiff';
import { courtApi } from '../services/api';
import LanguageToggle from './LanguageToggle';
import UserProfile from './UserProfile';
import BuyCreditSheet from './BuyCreditSheet';
import GiftIcon from './icons/GiftIcon';
import { sportGlyph } from '../utils/sportGlyph';

const UserHeader = () => {
  const { user, loading, error, isAuthenticated, forceRefresh } = useUser();
  const { t } = useLanguage();
  const { liff } = useLiff();
  const [buyOpen, setBuyOpen] = useState(false);
  const [courtTypes, setCourtTypes] = useState([]);

  useEffect(() => {
    courtApi.getCourtTypes().then(setCourtTypes).catch(() => {});
  }, []);

  // Promotion deep link (LINE OA rich menu ?promo=1, alias ?topup=1). Stash the
  // intent so it survives login, then open the sheet once authenticated.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('promo') === '1' || params.get('topup') === '1') {
      try { window.sessionStorage.setItem('pendingPromo', '1'); } catch (_) {}
      params.delete('promo');
      params.delete('topup');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    }
    let pending = false;
    try { pending = window.sessionStorage.getItem('pendingPromo') === '1'; } catch (_) {}
    if (pending && isAuthenticated) {
      setBuyOpen(true);
      try { window.sessionStorage.removeItem('pendingPromo'); } catch (_) {}
    }
  }, [isAuthenticated]);

  const nameOf = (id) => courtTypes.find((c) => c.id === id)?.name || id;
  // Sort balances by amount, most hours first.
  const balances = Object.entries(user?.tokens || {}).sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0));

  return (
    // Expanded floating banner. Top row: identity + flag toggle + เติมชั่วโมง.
    // Bottom row: named balances (full width). Pages reserve top room (pt-44).
    <header className="fixed inset-x-0 top-0 z-50">
      {/* App-bar: edge-to-edge, flush to the top, rounded only at the bottom —
          reads as a header, not a floating card. Content is width-capped. */}
      {(loading || error || isAuthenticated) && (
        <div className="bg-white/85 backdrop-blur-xl shadow-lg shadow-black/10 rounded-b-[1.75rem] border-b border-black/5">
          <div className="relative mx-auto max-w-3xl px-4 sm:px-6 pt-2 pb-3">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin" />
              <span className="text-sm">{t('loading') || 'Loading...'}</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-red-500">
              <span className="text-sm">{t('error') || 'Error'}</span>
              <button onClick={forceRefresh} className="px-2 py-1 bg-red-500 text-white text-xs rounded">
                {t('retry') || 'Retry'}
              </button>
            </div>
          ) : (
            <div>
              {/* Row A: identity (left) and flag+เติมชั่วโมง (right) span the SAME
                  height — items-stretch + justify-between pins flag to top,
                  button to bottom, matching the profile block's extent. */}
              <div className="flex items-stretch justify-between gap-3">
                <div className="min-w-0 flex items-center">
                  <UserProfile user={user} />
                </div>
                <div className="shrink-0 flex flex-col items-end justify-between">
                  <LanguageToggle size="sm" />
                  <button
                    onClick={() => setBuyOpen(true)}
                    aria-label={t('promotion') || 'Promotion'}
                    className="flex items-center gap-1.5 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-purple-600 px-3.5 py-2 text-white shadow-md shadow-purple-500/30 active:scale-95 transition"
                  >
                    <GiftIcon className="w-5 h-5" />
                    <span className="text-sm font-bold whitespace-nowrap">{t('promotion') || 'Promotion'}</span>
                  </button>
                </div>
              </div>

              {/* Row B: heading sits INLINE with the balance pills (one line, no
                  wasted heading row); scrolls sideways if there are many types. */}
              <div className="mt-1 pt-1 border-t border-gray-200/60 flex items-center gap-2.5 overflow-x-auto no-scrollbar">
                <span className="shrink-0 text-xs font-medium text-gray-400">
                  {t('your_tokens') || 'Hours remaining'}
                </span>
                {balances.length > 0 ? (
                  balances.map(([id, amt]) => (
                    <span key={id} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-sm">
                      <span>{sportGlyph(nameOf(id))}</span>
                      <span className="text-gray-500">{nameOf(id)}</span>
                      <span className="font-bold text-gray-900">{Number(amt).toFixed(1)}</span>
                      <span className="text-xs text-gray-400">ชม.</span>
                    </span>
                  ))
                ) : (
                  <span className="shrink-0 text-sm text-gray-400">0 ชม.</span>
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      <BuyCreditSheet
        open={buyOpen}
        idToken={user?.idToken}
        liff={liff}
        userTokens={user?.tokens}
        onClose={() => setBuyOpen(false)}
      />
    </header>
  );
};

export default UserHeader;
